import { useCallback, useEffect, useRef } from 'react';
import styles from './Scanner.styles';
import { selectStream } from './utils';

const BARCODE_FORMATS = ['code_128', 'ean_13', 'ean_8'];
interface ScannerProps {
  onDetected: (value: string) => void;
  showError: (err: string) => void;
}

const Scanner = (props: ScannerProps) => {
  const { onDetected, showError } = props;
  const videoRef = useRef<HTMLVideoElement>(null);

  const startVideo = useCallback(
    async (video: HTMLVideoElement) => {
      try {
        const stream = await selectStream();

        video.srcObject = stream;
        video.setAttribute('playsinline', 'true');
        video.play();

        const barcodeDetector = new window.BarcodeDetector({
          formats: BARCODE_FORMATS,
        });

        const detectBarcode = async () => {
          if (video.readyState === video.HAVE_ENOUGH_DATA) {
            try {
              const barcodes = await barcodeDetector.detect(video);

              if (barcodes.length > 0) {
                onDetected(barcodes[0].rawValue);
                stream.getTracks().forEach((track) => track.stop());
              }
            } catch (err) {
              console.error('Barcode detection error:', err);
            }
          }

          requestAnimationFrame(detectBarcode);
        };

        requestAnimationFrame(detectBarcode);
      } catch (err) {
        showError(err instanceof Error ? err.message : String(err));
      }
    },
    [onDetected, showError],
  );

  useEffect(() => {
    if (!('BarcodeDetector' in window)) {
      showError('BarcodeDetector API não está disponível neste navegador.');
      return;
    }

    const video = videoRef.current;

    if (!video) return;

    startVideo(video);

    return () => {
      if (video.srcObject) {
        const stream = video.srcObject as MediaStream;
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [startVideo, showError]);

  return (
    <video className={styles()} ref={videoRef} data-testid="barcode-scanner">
      <track kind="captions" />
    </video>
  );
};

export default Scanner;
